/**
 * MaturityBadge — Aperçu gratuit du score de maturité du dossier (sans IA payante).
 *
 * Affiché en haut de la page Workspace pour donner immédiatement à l'utilisateur
 * une lecture de son dossier — et un incitatif visuel à passer au rapport complet.
 *
 * 5 niveaux (du backend `_compute_maturity`) :
 *   incomplet · construction · exploitable · pret · avance
 *
 * Strictement quantitatif et factuel — aucune qualification juridique.
 */
import { useEffect, useState } from "react";
import { Sparkles, TrendingUp } from "lucide-react";
import { apiClient } from "@/lib/api";

const LEVEL_META = {
  incomplet:    { color: "#9CA3AF", trackPct: 12,  label: "Dossier incomplet" },
  construction: { color: "#D97706", trackPct: 32,  label: "Dossier en construction" },
  exploitable:  { color: "#C9A961", trackPct: 58,  label: "Dossier exploitable" },
  pret:         { color: "#059669", trackPct: 82,  label: "Dossier prêt à consultation" },
  avance:       { color: "#0B3A2A", trackPct: 100, label: "Dossier avancé" },
};

export default function MaturityBadge({ accessToken, refreshKey = 0, hasAudit = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.get(`/workspace/${accessToken}/maturity`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [accessToken, refreshKey]);

  if (loading || !data?.maturity) return null;

  const m = data.maturity;
  const meta = LEVEL_META[m.level] || LEVEL_META.construction;
  const details = m.details || {};

  return (
    <section
      className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5"
      data-testid="maturity-badge"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
            État du dossier
          </p>
          <h2
            className="font-serif italic text-[#0B3A2A] text-lg leading-tight mt-0.5"
            data-testid="maturity-level-label"
            style={{ color: meta.color }}
          >
            {meta.label}
          </h2>
        </div>
        <div className="text-right">
          <p className="font-serif text-2xl text-[#0B3A2A] leading-none" data-testid="maturity-score">
            {m.score}<span className="text-sm text-slate-400">/8</span>
          </p>
          <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 mt-0.5">indice</p>
        </div>
      </div>

      {/* Barre de progression institutionnelle */}
      <div className="relative h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${meta.trackPct}%`, backgroundColor: meta.color }}
        />
      </div>

      {/* Détails factuels */}
      <div className="grid grid-cols-4 gap-2 mt-4 text-center">
        {[
          ["Pièces", details.pieces_count],
          ["Catégories", details.distinct_categories],
          ["Datées", details.dated],
          ["Événements", details.timeline_events],
        ].map(([k, v]) => (
          <div key={k} className="bg-[#FAF7F1] rounded-lg py-2">
            <p className="text-[13px] font-semibold text-[#0B3A2A]">{v ?? 0}</p>
            <p className="text-[9px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">{k}</p>
          </div>
        ))}
      </div>

      {/* Suggestions courtes */}
      {data.suggestions?.length > 0 && (
        <ul className="mt-4 space-y-1.5">
          {data.suggestions.map((s, i) => (
            <li key={i} className="text-[12px] text-slate-600 flex items-start gap-2">
              <TrendingUp className="w-3 h-3 text-[#C9A961] mt-0.5 shrink-0" />
              {s}
            </li>
          ))}
        </ul>
      )}

      {/* Incitation rapport complet (masquée si l'audit a déjà été commandé) */}
      {!hasAudit && (
        <div className="mt-4 flex items-center justify-between gap-3 bg-gradient-to-r from-[#0B3A2A]/5 to-[#C9A961]/10 border border-[#C9A961]/30 rounded-xl px-4 py-3">
          <div className="flex items-start gap-2.5">
            <Sparkles className="w-4 h-4 text-[#C9A961] mt-0.5 shrink-0" />
            <div>
              <p className="text-[12px] text-[#0B3A2A] font-medium leading-tight">
                Passez à un dossier prêt à présenter
              </p>
              <p className="text-[11px] text-slate-600 italic mt-0.5">
                Synthèse · chronologie · profils potentiellement adaptés — 89,00 € TTC
              </p>
            </div>
          </div>
          <a
            href="#audit-cta"
            data-testid="maturity-to-audit-link"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('[data-testid="audit-cta-panel"], [data-testid="audit-ready-panel"], [data-testid="audit-generating-panel"], [data-testid="audit-pending-payment-panel"]')
                ?.scrollIntoView({ behavior: "smooth", block: "center" });
            }}
            className="shrink-0 text-[11px] text-[#0B3A2A] hover:text-[#8A6E3A] font-medium px-2 py-1 rounded-full border border-[#C9A961]/40 hover:border-[#C9A961] bg-white transition"
          >
            Voir le rapport →
          </a>
        </div>
      )}
    </section>
  );
}
